import { Variant } from '~/@types/product'

import { SerializedProduct } from '~/data/product-card-data/types'

/**
 * This function returns an array of all variants for a given product, including variants from
 * customization and label products.
 * @param {SerializedProduct} product - SerializedProduct, which is an object containing information
 * about a product, including its variants, customization products, and label customization products.
 * @returns The function `getBundleProductVariants` returns an array of `Variant` objects. The variants
 * are obtained by concatenating the variants of the main product, the customization products, and the
 * label customization products of a given `SerializedProduct`.
 */
export function getBundleProductVariants(
  product: SerializedProduct,
): Variant[] {
  const productVariant = product?.variants ?? []
  const customizationVariants =
    product?.customizationProducts
      ?.map((customizationProduct) => customizationProduct?.variants)
      ?.flat()
      ?.filter(Boolean) ?? []
  const labelVariants =
    product?.labelCustomizationProducts
      ?.map((labelProduct) => labelProduct.variants)
      ?.flat()
      .filter(Boolean) ?? []

  const variants = []
    .concat(productVariant)
    .concat(customizationVariants)
    .concat(labelVariants)

  return variants
}

/**
 * This function retrieves all variants from a Shopify product, including bundle and customization
 * products.
 * @param product - The `product` parameter is an object that represents a product in a Shopify store.
 * It contains information such as the product's title, description, price, and variants. The function
 * `getAllVariantsFromShopifyProduct` takes this product object as input and returns an array of all
 * the variants that
 * @returns The function `getAllVariantsFromShopifyProduct` returns an array of all variants that might
 * be in a Shopify product, including variants from bundle products (which may contain customization
 * products and label products). The function first gets the variants of the main product using the
 * `getBundleProductVariants` function, and then gets the variants of any pack products (if they exist)
 * by iterating through each pack
 */
export function getAllVariantsFromShopifyProduct(
  product: SerializedProduct,
): Variant[] {
  const mainProductVariants = getBundleProductVariants(product)

  const packVariants =
    product?.packProducts
      ?.reduce((acc, tab) => {
        const tabVariants =
          tab?.reduce((packAcc, packProduct) => {
            const variants = getBundleProductVariants(packProduct)
            packAcc = packAcc.concat(variants)
            return packAcc
          }, [] as Variant[]) ?? []

        acc = acc.concat(tabVariants)
        return acc.flat()
      }, [] as Variant[])
      .flat() ?? []

  return mainProductVariants?.concat(packVariants)
}
